import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Plus, Trash2, FileText, ArrowLeft, Eye, Download,
  Save, Edit2, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import DatePickerField from '../components/DatePickerField';
import { TvaToggle } from '../components/TvaToggle';
const tvaStateOf = (f) => ({ on: f.tva_on ?? (Number(f.tva_pct) > 0), pct: Number(f.tva_pct) > 0 ? f.tva_pct : 18 });
const tvaEffOf = (f) => { const t = tvaStateOf(f); return t.on ? (parseFloat(t.pct) || 0) : 0; };


const fmt = (n) => Math.round(parseFloat(n) || 0).toLocaleString('fr-FR');
const today = () => new Date().toISOString().split('T')[0];

// Calcule le nombre de jours INCLUS entre 2 dates ISO (YYYY-MM-DD)
const daysBetween = (debut, fin) => {
  if (!debut || !fin) return 1;
  try {
    const d1 = new Date(debut);
    const d2 = new Date(fin);
    const diff = Math.floor((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 1;
  } catch {
    return 1;
  }
};
const lineMontant = (l) =>
  (parseFloat(l.prix_unitaire_ht) || 0) *
  (parseFloat(l.quantite) || 0) *
  daysBetween(l.periode_debut, l.periode_fin);

const newLine = () => ({
  id: Date.now().toString() + Math.random(),
  designation: '', type_panneau: '', emplacement: '',
  periode_debut: today(), periode_fin: today(),
  quantite: 1, unite: 'Forfait', prix_unitaire_ht: 0,
});

const STATUT_OPTIONS = [
  { v: 'brouillon', label: 'Brouillon', color: 'bg-gray-200 text-gray-700' },
  { v: 'envoyee', label: 'Envoyée', color: 'bg-blue-100 text-blue-700' },
  { v: 'payee', label: 'Payée', color: 'bg-green-100 text-green-700' },
  { v: 'retard', label: 'En retard', color: 'bg-red-100 text-red-700' },
];

export default function Factures({ user, onLogout }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [factures, setFactures] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');
  const [editing, setEditing] = useState(null); // facture en cours d'édition
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [fs, cs] = await Promise.all([
        axios.get(`${API}/api/factures`),
        axios.get(`${API}/api/clients`).catch(() => ({ data: { items: [] } })),
      ]);
      setFactures(fs.data?.items || []);
      setClients(cs.data?.items || cs.data?.clients || cs.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Si on arrive avec ?action=new ou ?client_id=...
  useEffect(() => {
    const action = searchParams.get('action');
    const clientId = searchParams.get('client_id');
    if (action === 'new' || clientId) openNew(clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), clients]);

  const openNew = async (clientId = null) => {
    const numRes = await axios.get(`${API}/api/factures/next-number`).catch(() => null);
    const numero = numRes?.data?.numero || '';
    const cli = clientId ? clients.find((c) => c.id === clientId) : null;
    const draft = {
      id: null,
      numero,
      client_id: clientId || '',
      client: cli ? {
        id: cli.id,
        nom: cli.nom || cli.entreprise || cli.name || '',
        adresse: cli.adresse || '',
        nif: cli.nif || '',
        email: cli.email || '',
        telephone: cli.telephone || cli.phone || '',
      } : { nom: '', adresse: '', nif: '', email: '', telephone: '' },
      date_facture: today(),
      date_echeance: today(),
      objet: "Location d'espaces publicitaires sur panneaux digitaux",
      categorie: searchParams.get('categorie') || 'panneaux',
      lignes: [newLine()],
      tva_pct: 18,
      tva_on: false,
      statut: 'brouillon',
      montant_paye: 0,
      mode_paiement: '',
      date_paiement: '',
      ref_paiement: '',
      notes: '',
    };
    setEditing(draft);
  };

  const updateLine = (id, field, value) => {
    setEditing((d) => ({
      ...d,
      lignes: d.lignes.map((l) => l.id === id ? { ...l, [field]: value } : l),
    }));
  };
  const addLine = () => setEditing((d) => ({ ...d, lignes: [...d.lignes, newLine()] }));
  const removeLine = (id) => setEditing((d) => ({ ...d, lignes: d.lignes.filter((l) => l.id !== id) }));

  const totals = (() => {
    if (!editing) return { ht: 0, tva: 0, ttc: 0 };
    const ht = editing.lignes.reduce((s, l) => s + lineMontant(l), 0);
    const tva = ht * tvaEffOf(editing) / 100;
    return { ht, tva: Math.round(tva), ttc: Math.round(ht + tva) };
  })();

  const saveFacture = async () => {
    if (!editing) return;
    try {
      const payload = {
        numero: editing.numero,
        client_id: editing.client_id || null,
        client: editing.client,
        date_facture: editing.date_facture,
        date_echeance: editing.date_echeance,
        objet: editing.objet,
        tva_pct: tvaEffOf(editing),
        lignes: editing.lignes.map((l) => ({
          designation: l.designation, type_panneau: l.type_panneau,
          emplacement: l.emplacement,
          periode_debut: l.periode_debut, periode_fin: l.periode_fin,
          quantite: parseFloat(l.quantite) || 0,
          unite: l.unite || 'Forfait',
          prix_unitaire_ht: parseFloat(l.prix_unitaire_ht) || 0,
          montant_ht: lineMontant(l),  // = prix × quantité × jours
        })),
        statut: editing.statut,
        montant_paye: parseFloat(editing.montant_paye) || 0,
        mode_paiement: editing.mode_paiement,
        date_paiement: editing.date_paiement || null,
        ref_paiement: editing.ref_paiement,
        notes: editing.notes,
      };
      if (editing.id) {
        await axios.put(`${API}/api/factures/${editing.id}`, payload);
        toast.success('Facture mise à jour');
      } else {
        await axios.post(`${API}/api/factures`, payload);
        toast.success('Facture créée');
      }
      setEditing(null);
      loadAll();
    } catch (e) {
      console.error(e);
      toast.error("Erreur à l'enregistrement");
    }
  };

  const previewSavedFacture = async (f) => {
    try {
      const res = await axios.get(`${API}/api/factures/${f.id}/pdf`, { responseType: 'blob' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })));
      setPreviewMeta({ id: f.id, numero: f.numero });
    } catch (e) {
      toast.error("Erreur d'aperçu");
    }
  };

  const previewDraft = async () => {
    if (!editing) return;
    try {
      const res = await axios.post(`${API}/api/factures/preview`, {
        ...editing,
        lignes: editing.lignes.map((l) => ({
          ...l,
          quantite: parseFloat(l.quantite) || 0,
          prix_unitaire_ht: parseFloat(l.prix_unitaire_ht) || 0,
          montant_ht: lineMontant(l),
        })),
      }, { responseType: 'blob' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })));
      setPreviewMeta({ id: null, numero: editing.numero });
    } catch (e) {
      console.error(e);
      toast.error("Erreur d'aperçu");
    }
  };

  const downloadFacture = async (f) => {
    try {
      const res = await axios.get(`${API}/api/factures/${f.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${f.numero.replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur téléchargement');
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewMeta(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/api/factures/${deleteTarget.id}`);
      toast.success('Facture supprimée');
      setDeleteTarget(null);
      loadAll();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  // Filtres
  const filtered = factures.filter((f) => {
    if (filterStatut !== 'all' && (f.statut || '').toLowerCase() !== filterStatut) return false;
    if (search) {
      const q = search.toLowerCase();
      const txt = `${f.numero || ''} ${f.client?.nom || ''} ${f.objet || ''}`.toLowerCase();
      if (!txt.includes(q)) return false;
    }
    return true;
  });

  const statutBadge = (st) => {
    const opt = STATUT_OPTIONS.find((o) => o.v === (st || '').toLowerCase());
    return (
      <Badge className={`${opt?.color || 'bg-gray-100 text-gray-700'} font-medium`}>
        {opt?.label || st || 'Brouillon'}
      </Badge>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="factures-page">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={() => navigate(-1)} variant="outline" className="gap-2" data-testid="factures-back-btn">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="h-7 w-7 text-blue-600" /> Factures
            </h1>
            <p className="text-gray-600 mt-1 text-sm">{factures.length} facture(s) au total</p>
          </div>
          <Button onClick={() => openNew()} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="new-facture-btn">
            <Plus className="h-4 w-4" /> Créer une facture
          </Button>
        </div>

        {/* Filtres */}
        <Card>
          <CardContent className="p-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Recherche</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder="N° facture, client, objet..." className="pl-8" data-testid="factures-search" />
              </div>
            </div>
            <div className="w-48">
              <Label className="text-xs">Statut</Label>
              <Select value={filterStatut} onValueChange={setFilterStatut}>
                <SelectTrigger data-testid="filter-statut"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {STATUT_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Liste */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Liste des factures</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-blue-50 text-blue-900 border-b border-blue-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">N°</th>
                    <th className="px-3 py-2 text-left font-semibold">Date</th>
                    <th className="px-3 py-2 text-left font-semibold">Client</th>
                    <th className="px-3 py-2 text-right font-semibold">Total TTC</th>
                    <th className="px-3 py-2 text-center font-semibold">Statut</th>
                    <th className="px-3 py-2 w-40 text-center font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (<tr><td colSpan="6" className="p-8 text-center text-gray-400">Chargement...</td></tr>)}
                  {!loading && filtered.length === 0 && (
                    <tr><td colSpan="6" className="p-8 text-center text-gray-400 italic">Aucune facture</td></tr>
                  )}
                  {filtered.map((f) => (
                    <tr key={f.id} className="border-b hover:bg-blue-50/40" data-testid={`facture-row-${f.id}`}>
                      <td className="px-3 py-2 font-bold text-blue-700">{f.numero}</td>
                      <td className="px-3 py-2">{f.date_facture}</td>
                      <td className="px-3 py-2 font-medium">{f.client?.nom || '—'}</td>
                      <td className="px-3 py-2 text-right font-bold text-blue-900 whitespace-nowrap">
                        {fmt(f.total_ttc)} FCFA
                      </td>
                      <td className="px-3 py-2 text-center">{statutBadge(f.statut)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-purple-600 hover:bg-purple-100"
                                  onClick={() => previewSavedFacture(f)} title="Aperçu" data-testid={`preview-fac-${f.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100"
                                  onClick={() => downloadFacture(f)} title="Télécharger PDF">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-100"
                                  onClick={() => setEditing({ ...f, lignes: f.lignes?.length ? f.lignes.map((l) => ({ ...l, id: l.id || Date.now() + Math.random() })) : [newLine()] })}
                                  title="Modifier">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                                  onClick={() => setDeleteTarget(f)} title="Supprimer">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Modal Édition */}
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-6xl w-full h-[92vh] flex flex-col p-0" data-testid="facture-edit-modal">
            <DialogHeader className="px-6 py-3 border-b bg-blue-50">
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                {editing?.id ? `Modifier facture ${editing.numero}` : `Nouvelle facture ${editing?.numero || ''}`}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {editing && (
                <>
                  {/* Header form */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">N° Facture</Label>
                      <Input value={editing.numero} onChange={(e) => setEditing({ ...editing, numero: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Date Facture</Label>
                      <Input type="date" value={editing.date_facture} onChange={(e) => setEditing({ ...editing, date_facture: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Date Échéance</Label>
                      <Input type="date" value={editing.date_echeance} onChange={(e) => setEditing({ ...editing, date_echeance: e.target.value })} />
                    </div>
                  </div>
                  {/* Client */}
                  <Card className="bg-blue-50/30 border-blue-200">
                    <CardContent className="p-4 space-y-3">
                      <h3 className="font-semibold text-blue-900">Facturé à</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Sélectionner un client existant</Label>
                          <Select value={editing.client_id || ''} onValueChange={(v) => {
                            const cli = clients.find((c) => c.id === v);
                            setEditing({
                              ...editing,
                              client_id: v,
                              client: cli ? {
                                id: cli.id,
                                nom: cli.nom || cli.entreprise || cli.name || '',
                                adresse: cli.adresse || '',
                                nif: cli.nif || '',
                                email: cli.email || '',
                                telephone: cli.telephone || cli.phone || '',
                              } : editing.client,
                            });
                          }}>
                            <SelectTrigger><SelectValue placeholder="— Saisie manuelle —" /></SelectTrigger>
                            <SelectContent>
                              {clients.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.nom || c.entreprise || c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Société *</Label>
                          <Input value={editing.client?.nom || ''} onChange={(e) => setEditing({ ...editing, client: { ...editing.client, nom: e.target.value } })} />
                        </div>
                        <div>
                          <Label className="text-xs">NIF</Label>
                          <Input value={editing.client?.nif || ''} onChange={(e) => setEditing({ ...editing, client: { ...editing.client, nif: e.target.value } })} />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs">Adresse</Label>
                          <Input value={editing.client?.adresse || ''} onChange={(e) => setEditing({ ...editing, client: { ...editing.client, adresse: e.target.value } })} />
                        </div>
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input value={editing.client?.email || ''} onChange={(e) => setEditing({ ...editing, client: { ...editing.client, email: e.target.value } })} />
                        </div>
                        <div>
                          <Label className="text-xs">Téléphone</Label>
                          <Input value={editing.client?.telephone || ''} onChange={(e) => setEditing({ ...editing, client: { ...editing.client, telephone: e.target.value } })} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div>
                    <Label className="text-xs">Objet de la facture</Label>
                    <Input value={editing.objet} onChange={(e) => setEditing({ ...editing, objet: e.target.value })} />
                  </div>

                  {/* Lignes */}
                  <Card>
                    <CardHeader className="bg-blue-100 py-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm text-blue-900">Lignes de facturation</CardTitle>
                        <Button size="sm" onClick={addLine} className="gap-1 bg-blue-600 hover:bg-blue-700"><Plus className="h-3 w-3" /> Ligne</Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-blue-50 border-b">
                            <tr>
                              <th className="px-2 py-2 text-left">Désignation</th>
                              <th className="px-2 py-2 text-left">Type panneau</th>
                              <th className="px-2 py-2 text-left">Emplacement</th>
                              <th className="px-2 py-2 text-left w-32">Période début</th>
                              <th className="px-2 py-2 text-left w-32">Période fin</th>
                              <th className="px-2 py-2 text-center w-16">Jours</th>
                              <th className="px-2 py-2 text-left w-20">Quantité</th>
                              <th className="px-2 py-2 text-left w-24">Unité</th>
                              <th className="px-2 py-2 text-right w-32">Prix unit. HT<br/><span className="text-[10px] text-gray-500">(par jour)</span></th>
                              <th className="px-2 py-2 text-right w-32">Montant HT</th>
                              <th className="w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {editing.lignes.map((l) => {
                              const j = daysBetween(l.periode_debut, l.periode_fin);
                              const m = lineMontant(l);
                              return (
                                <tr key={l.id} className="border-b">
                                  <td className="p-1"><Input value={l.designation} onChange={(e) => updateLine(l.id, 'designation', e.target.value)} className="h-8 text-xs" /></td>
                                  <td className="p-1"><Input value={l.type_panneau} onChange={(e) => updateLine(l.id, 'type_panneau', e.target.value)} className="h-8 text-xs" placeholder="4x3, LED..." /></td>
                                  <td className="p-1"><Input value={l.emplacement} onChange={(e) => updateLine(l.id, 'emplacement', e.target.value)} className="h-8 text-xs" /></td>
                                  <td className="p-1">
                                    <Input
                                      type="date"
                                      value={l.periode_debut || ''}
                                      onChange={(e) => updateLine(l.id, 'periode_debut', e.target.value)}
                                      className="h-8 text-xs"
                                      data-testid={`line-debut-${l.id}`}
                                    />
                                  </td>
                                  <td className="p-1">
                                    <Input
                                      type="date"
                                      value={l.periode_fin || ''}
                                      onChange={(e) => updateLine(l.id, 'periode_fin', e.target.value)}
                                      className="h-8 text-xs"
                                      data-testid={`line-fin-${l.id}`}
                                    />
                                  </td>
                                  <td className="p-1 text-center text-xs font-bold text-blue-700 bg-blue-50/50" data-testid={`line-jours-${l.id}`}>{j}</td>
                                  <td className="p-1"><Input type="number" value={l.quantite} onChange={(e) => updateLine(l.id, 'quantite', e.target.value)} className="h-8 text-xs text-right" /></td>
                                  <td className="p-1"><Input value={l.unite} onChange={(e) => updateLine(l.id, 'unite', e.target.value)} className="h-8 text-xs" /></td>
                                  <td className="p-1"><Input type="number" value={l.prix_unitaire_ht} onChange={(e) => updateLine(l.id, 'prix_unitaire_ht', e.target.value)} className="h-8 text-xs text-right font-semibold" /></td>
                                  <td className="p-1 text-right font-semibold text-blue-700" data-testid={`line-montant-${l.id}`}>{fmt(m)}</td>
                                  <td className="p-1">
                                    {editing.lignes.length > 1 && (
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeLine(l.id)}><Trash2 className="h-3 w-3" /></Button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Totaux + paiement */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="p-4 space-y-3">
                        <h3 className="font-semibold text-sm">Paiement</h3>
                        <div>
                          <Label className="text-xs">Statut</Label>
                          <Select value={editing.statut} onValueChange={(v) => setEditing({ ...editing, statut: v })}>
                            <SelectTrigger data-testid="edit-statut"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STATUT_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Mode de paiement</Label>
                          <Input value={editing.mode_paiement} onChange={(e) => setEditing({ ...editing, mode_paiement: e.target.value })} placeholder="Virement, Espèces..." />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Date de paiement</Label>
                            <Input type="date" value={editing.date_paiement || ''} onChange={(e) => setEditing({ ...editing, date_paiement: e.target.value })} />
                          </div>
                          <div>
                            <Label className="text-xs">Référence</Label>
                            <Input value={editing.ref_paiement} onChange={(e) => setEditing({ ...editing, ref_paiement: e.target.value })} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 space-y-2">
                        <h3 className="font-semibold text-sm">Totaux</h3>
                        <div className="flex justify-between text-sm"><span>Total HT</span><span className="font-bold">{fmt(totals.ht)} FCFA</span></div>
                        <TvaToggle tva={tvaStateOf(editing)} base={totals.ht} testid="facture-edit-tva"
                          setTva={(fn) => setEditing((e) => { const t = fn(tvaStateOf(e)); return { ...e, tva_on: t.on, tva_pct: t.pct }; })} />
                        {totals.tva > 0 && <div className="flex justify-between text-sm"><span>TVA ({tvaEffOf(editing)} %)</span><span className="font-bold">{fmt(totals.tva)} FCFA</span></div>}
                        <div className="flex justify-between text-base bg-blue-600 text-white px-3 py-2 rounded">
                          <span className="font-semibold">Total TTC</span><span className="font-bold">{fmt(totals.ttc)} FCFA</span>
                        </div>
                        <div>
                          <Label className="text-xs">Montant payé</Label>
                          <Input type="number" value={editing.montant_paye} onChange={(e) => setEditing({ ...editing, montant_paye: e.target.value })} />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </div>
            <DialogFooter className="px-6 py-3 border-t bg-white gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
              <Button variant="outline" onClick={previewDraft} className="gap-2"><Eye className="h-4 w-4" /> Aperçu</Button>
              <Button onClick={saveFacture} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="save-facture-btn">
                <Save className="h-4 w-4" /> {editing?.id ? 'Mettre à jour' : 'Créer la facture'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Modal */}
        <Dialog open={!!previewUrl} onOpenChange={(o) => !o && closePreview()}>
          <DialogContent className="max-w-5xl w-full h-[92vh] flex flex-col p-0" data-testid="facture-preview-modal">
            <DialogHeader className="px-6 py-3 border-b">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-600" /> Aperçu — {previewMeta?.numero || ''}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 bg-gray-100 overflow-hidden">
              {previewUrl && <iframe src={previewUrl} title="Aperçu facture" className="w-full h-full border-0" />}
            </div>
            <div className="px-6 py-3 border-t flex justify-end gap-2 bg-white">
              <Button variant="outline" onClick={closePreview}>Fermer</Button>
              {previewMeta?.id && (
                <Button onClick={() => downloadFacture({ id: previewMeta.id, numero: previewMeta.numero })}
                        className="gap-2 bg-blue-600 hover:bg-blue-700">
                  <Download className="h-4 w-4" /> Télécharger
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Supprimer la facture ?</DialogTitle>
            </DialogHeader>
            <p className="py-2">Voulez-vous vraiment supprimer la facture <b>{deleteTarget?.numero}</b> ? Cette action est irréversible.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annuler</Button>
              <Button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white gap-2">
                <Trash2 className="h-4 w-4" /> Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
